import { Link, useParams } from 'react-router-dom';
import HtmlContent from '../components/HtmlContent';
import NotFoundView from '../components/NotFoundView';
import { useWikiPage, type WikiSection } from '../hooks/useWikiPage';
import { usePageTitle } from '../hooks/usePageTitle';
import { ApiClientError } from '../api/client';

interface WikiPageProps {
  section: WikiSection;
}

export default function WikiPage({ section }: WikiPageProps) {
  const params = useParams();
  const splat = params['*'] ?? '';

  const { data, error, isLoading } = useWikiPage(section, splat);
  const title = data?.title ?? splat.split('/').pop() ?? section;
  usePageTitle(title);

  if (isLoading) return <p className="muted">Loading...</p>;
  if (error instanceof ApiClientError && error.status === 404) {
    return <NotFoundView path={`/${section}/${splat}`} />;
  }
  if (error) return <p style={{ color: 'var(--danger)' }}>Error: {error.message}</p>;
  if (!data) return null;

  return (
    <article data-testid="wiki-page-content">
      {data.breadcrumb.length > 0 ? (
        <nav className="breadcrumb" data-testid="wiki-breadcrumb">
          {data.breadcrumb.map((crumb, idx) => (
            <span key={crumb.path}>
              {idx > 0 ? <span className="sep">/</span> : null}
              <Link to={crumb.path}>{crumb.label}</Link>
            </span>
          ))}
        </nav>
      ) : null}
      <h1 className="page-title">{data.title}</h1>
      <HtmlContent html={data.html} testId="wiki-page-html" />
    </article>
  );
}
